import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import EntityProperty from '@/common/models/EntityProperty';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { DASH, getStore, toServerDateTime } from '@/globals';
import { queryKeys } from '@/org-app/services/tanstack/queryKeys';
import rootStore from '@/store';
import {
  UPDATE_NETWORK_CODE,
  UPDATE_NETWORK_LOOP_LENGTH,
  UPDATE_NETWORK_NAME,
  UPDATE_NETWORK_SPOT_LENGTH,
} from '@/store/modules/models/mutation-types';
import formatNumber from '@/utils/NumberFormatter';
import Validatable, { IEntityChange, IGetEntityChanges } from '@/validator/Validatable';
import i18n from '@/vueI18n';

const type = 'Network';

export interface INetworkAPI {
  id: number;
  name: string;
  owner_id: number;
  active: boolean;
  archived: boolean;
  code: string;
  network_id: number;
  created_on_utc: string;
  modified_on_utc: string;
  spot_length?: number;
  loop_length?: number;
}

export interface INetwork {
  name: string;
  ownerID: number;
  isActive: boolean;
  isArchived: boolean;
  code: string;
  networkID: number;
  createdOnUtc?: DateTime;
  modifiedOnUtc?: DateTime;
  mediaOwnerName: string;
  apiObject: INetworkAPI;
  spotLength?: number;
  loopLength?: number;
}

export default class Network extends Validatable<Network> implements INetwork, IGetEntityChanges<Network> {
  apiObject: INetworkAPI;
  cachedOriginalObject: Network;

  name: string;
  ownerID: number;
  isActive: boolean;
  isArchived: boolean;
  code: string;
  networkID: number;
  createdOnUtc?: DateTime;
  modifiedOnUtc?: DateTime;
  mediaOwnerName: string;
  spotLength?: number;
  loopLength?: number;

  constructor(
    id = -1,
    name = '',
    ownerID?: number,
    isActive = false,
    isArchived = false,
    code = '',
    networkID?: number,
    createdOnUtc?: DateTime,
    modifiedOnUtc?: DateTime,
    mediaOwnerName?: string,
    spotLength?: number,
    loopLength?: number,
  ) {
    super();
    Network.queryKeys = { getAll: queryKeys.networks };
    this.id = id;
    this.name = name;
    this.ownerID = ownerID;
    this.isActive = isActive;
    this.isArchived = isArchived;
    this.code = code;
    this.networkID = networkID;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.mediaOwnerName = mediaOwnerName;
    this.spotLength = spotLength;
    this.loopLength = loopLength;

    this.apiObject = null;
  }

  static init() {
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Network.entityPropertiesCached = null;
    });
  }

  get originalObject(): Network {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = Network.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  get endpoint(): string {
    return `/networks/${this.id}`;
  }

  get isActiveDescription(): string {
    return this.isActive ? capitalize(i18n.global.t('actions.yes')) : capitalize(i18n.global.t('actions.no'));
  }

  get isArchivedDescription(): string {
    return this.isArchived ? capitalize(i18n.global.t('actions.yes')) : capitalize(i18n.global.t('actions.no'));
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get spotLengthFormatted(): string {
    return this.spotLength != null && !Number.isNaN(this.spotLength) ? formatNumber(this.spotLength) : DASH;
  }

  get loopLengthFormatted(): string {
    return this.loopLength != null && !Number.isNaN(this.loopLength) ? formatNumber(this.loopLength) : DASH;
  }

  get createdOn(): DateTime | null {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal() : null;
  }

  get modifiedOn(): DateTime | null {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal() : null;
  }

  get idAndName(): string {
    return `${this.id} - ${this.name}`;
  }

  get formattedName(): string {
    const organizationSettings = getStore().state?.orgApp?.organizationSettings;
    let name =
      this.mediaOwnerName && organizationSettings?.uiEnableNetworksForHSBuyers
        ? `${this.name} (${this.mediaOwnerName})`
        : this.name;
    if (!this.isActive) name = `${name} [${i18n.global.t('states.inactive')}]`;
    return name;
  }

  async getChanges(): Promise<IEntityChange<Network>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<Network>(this.originalObject, Network.entityProperties);
  }

  static fromApi(apiObject: INetworkAPI): Network {
    const network = new Network(
      apiObject.network_id,
      apiObject.name,
      apiObject.owner_id,
      apiObject.active,
      apiObject.archived,
      apiObject.code,
      apiObject.network_id,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      null,
      apiObject.spot_length,
      apiObject.loop_length,
    );
    network.apiObject = apiObject;
    return network;
  }

  toApi(): INetworkAPI {
    return {
      id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      owner_id: this.ownerID,
      active: this.isActive,
      archived: this.isArchived,
      code: this.code,
      network_id: this.networkID,
      created_on_utc: toServerDateTime(this.createdOnUtc),
      modified_on_utc: toServerDateTime(this.modifiedOnUtc),
      spot_length: this.spotLength,
      loop_length: this.loopLength,
    };
  }

  static fromObject(obj: INetwork): Network {
    return super.fromObject(obj, new Network());
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      name: 'required|max:100|not_empty_or_whitespace',
      code: 'required|max:100|not_empty_or_whitespace',
    };
  }

  static get entityProperties(): { [key: string]: EntityProperty<Network> } {
    if (Network.entityPropertiesCached != null) {
      return Network.entityPropertiesCached;
    }

    const action = (network: Network) =>
      rootStore.dispatch('orgApp/updateNetwork', { instance: network, showLoader: false });

    Network.entityPropertiesCached = {
      name: new EntityProperty({
        label: capitalize(i18n.global.t('names.name', { n: 1 })),
        property: 'name',
        type: TableColumnTypes.string,
        editablePropertyName: 'name',
        mutation: UPDATE_NETWORK_NAME,
        editableClassName: type,
        action,
      }),
      code: new EntityProperty({
        label: capitalize(i18n.global.t('names.code', { n: 1 })),
        property: 'code',
        type: TableColumnTypes.string,
        editablePropertyName: 'code',
        mutation: UPDATE_NETWORK_CODE,
        editableClassName: type,
        action,
      }),
      spotLength: new EntityProperty({
        label: capitalize(i18n.global.t('names.spotLength', { n: 1 })),
        property: 'spot_length',
        type: TableColumnTypes.integer,
        editablePropertyName: 'spotLength',
        mutation: UPDATE_NETWORK_SPOT_LENGTH,
        editableClassName: type,
        action,
      }),
      loopLength: new EntityProperty({
        label: capitalize(i18n.global.t('names.loopLength', { n: 1 })),
        property: 'loop_length',
        type: TableColumnTypes.integer,
        editablePropertyName: 'loopLength',
        mutation: UPDATE_NETWORK_LOOP_LENGTH,
        editableClassName: type,
        action,
      }),
      isActive: new EntityProperty({
        label: capitalize(i18n.global.t('states.active', { n: 1 })),
        property: 'active',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'isActive',
      }),
      createdOn: new EntityProperty({
        label: capitalize(i18n.global.t('messages.createdOn')),
        property: 'created_on_utc',
        type: TableColumnTypes.datetime,
        editablePropertyName: 'createdOnUtc',
      }),
      modifiedOn: new EntityProperty({
        label: capitalize(i18n.global.t('messages.lastModifiedOn')),
        property: 'modified_on_utc',
        type: TableColumnTypes.datetime,
        editablePropertyName: 'modifiedOnUtc',
      }),
    };

    return Network.entityPropertiesCached;
  }
}
